import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bounce, toast } from "react-toastify";
import { Flame, Handshake, RefreshCw, Swords, Trophy, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getUserTeammates, getUserTeams } from "../api/teamApi";
import TeamCard from "../components/Teams/TeamCard";
import TeamCustomizeModal from "../components/Teams/TeamCustomizeModal";
import { MemberAvatar, SkeletonBlock, StatTile } from "../components/Teams/TeamUI";
import { getDisplayName } from "../components/Teams/teamIdentity";

const FILTERS = ["all", "PvP", "Coop", "PvE"];
const SORTS = ["recent", "played", "winRate"];

/** "Played with" / "Faced" list of players with a games count. */
const PlayerList = ({ title, icon: Icon, entries, renderMeta, emptyText }) => (
  <section className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
    <h2 className="flex items-center gap-2 text-base font-bold text-[#120239]">
      <Icon size={18} className="text-orange-500" aria-hidden="true" />
      {title}
    </h2>
    {entries.length === 0 ? (
      <p className="mt-3 text-sm text-gray-500">{emptyText}</p>
    ) : (
      <ul className="mt-3 space-y-2">
        {entries.map((entry) => (
          <li key={entry.user.id} className="flex items-center gap-3 rounded-2xl bg-gray-50 px-3 py-2">
            <MemberAvatar user={entry.user} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900">{getDisplayName(entry.user)}</p>
              <p className="truncate text-xs text-gray-500">@{entry.user.user_name || "-"}</p>
            </div>
            <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-600">{renderMeta(entry)}</span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const Teams = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [teams, setTeams] = useState([]);
  const [played, setPlayed] = useState({ teammates: [], opponents: [] });
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);

  const load = useCallback(async () => {
    if (!userData?.id) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const [teamsResponse, playedResponse] = await Promise.all([
        getUserTeams(userData.id, { sort }),
        getUserTeammates(userData.id, { limit: 5 }),
      ]);
      setTeams(teamsResponse.teams || []);
      setPlayed(playedResponse);
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [userData?.id, sort]);

  useEffect(() => {
    load();
  }, [load]);

  const soloTeam = teams.find((team) => team.isSolo);
  const visibleTeams = useMemo(
    () =>
      teams.filter(
        (team) =>
          !(filter === "all" && team.isSolo) &&
          (filter === "all" || team.stats.sessionTypes[filter] > 0)
      ),
    [teams, filter]
  );
  const totals = useMemo(
    () =>
      teams.reduce(
        (acc, team) => ({
          squads: acc.squads + (team.isSolo ? 0 : 1),
          wins: acc.wins + team.stats.wins,
          bestStreak: Math.max(acc.bestStreak, team.stats.bestWinStreak),
        }),
        { squads: 0, wins: 0, bestStreak: 0 }
      ),
    [teams]
  );

  const handleSaved = (updated, { renamed, keepOpen } = {}) => {
    setTeams((current) => current.map((team) => (team.id === updated.id ? updated : team)));
    if (keepOpen) return;
    setEditingTeam(null);
    toast.success(
      renamed
        ? t("teamRenamedToast", { defaultValue: "Say hello to {{name}}!", name: updated.teamName })
        : t("teamUpdatedToast", { defaultValue: "Team updated" }),
      { position: "top-center", autoClose: 3000, transition: Bounce }
    );
  };

  return (
    <div className="mx-auto max-w-6xl pb-16">
      {/* Hero */}
      <section className="overflow-hidden rounded-3xl bg-[#120239] text-white shadow-xl">
        <div className="relative px-6 py-8 md:px-10">
          <div
            className="pointer-events-none absolute -end-16 -top-16 h-56 w-56 rounded-full bg-[#FF9200]/30 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-orange-200">
                <Users size={14} aria-hidden="true" />
                {t("teamsEyebrow", { defaultValue: "Squads" })}
              </p>
              <h1 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl">
                {t("mySquads", { defaultValue: "My Squads" })}
              </h1>
              <p className="mt-2 max-w-xl text-sm text-slate-300 md:text-base">
                {t("mySquadsIntro", {
                  defaultValue:
                    "Every crew you've played with. Play again with the same players and you keep your team, its name and its record.",
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={load}
              disabled={isLoading}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 self-start rounded-full bg-white px-5 text-sm font-semibold text-[#120239] transition hover:bg-orange-50 disabled:opacity-60 md:self-auto"
            >
              <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} aria-hidden="true" />
              {t("refresh", { defaultValue: "Refresh" })}
            </button>
          </div>

          <div className="relative mt-6 grid grid-cols-3 gap-3">
            <StatTile dark icon={Users} label={t("squads", { defaultValue: "Squads" })} value={totals.squads} />
            <StatTile dark icon={Trophy} accent="text-amber-300" label={t("wins", { defaultValue: "Wins" })} value={totals.wins} />
            <StatTile dark icon={Flame} accent="text-orange-400" label={t("bestStreak", { defaultValue: "Best streak" })} value={totals.bestStreak} />
          </div>
        </div>
      </section>

      {loadError && (
        <div role="alert" className="mt-6 flex flex-col items-start gap-3 rounded-3xl bg-rose-50 p-5 text-rose-800 ring-1 ring-rose-200 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="min-h-[44px] rounded-full bg-rose-600 px-5 text-sm font-semibold text-white"
          >
            {t("tryAgain", { defaultValue: "Try again" })}
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2" role="group" aria-label={t("filterBySessionType", { defaultValue: "Filter by session type" })}>
          {FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
              className={`min-h-[44px] rounded-full px-4 text-sm font-semibold transition ${
                filter === value
                  ? "bg-[#120239] text-white shadow"
                  : "bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
              }`}
            >
              {value === "all" ? t("allSquads", { defaultValue: "All squads" }) : value}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          {t("sortBy", { defaultValue: "Sort by" })}
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            className="min-h-[44px] rounded-full border border-gray-300 bg-white px-4 text-sm"
          >
            {SORTS.map((value) => (
              <option key={value} value={value}>
                {t(`sort_${value}`, {
                  defaultValue: { recent: "Recently played", played: "Most played", winRate: "Win rate" }[value],
                })}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-6">
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {[0, 1, 2, 3].map((key) => (
                <SkeletonBlock key={key} className="h-56" />
              ))}
            </div>
          ) : (
            <>
              {filter === "all" && soloTeam && (
                <section>
                  <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">
                    {t("soloTeamHeading", { defaultValue: "Solo" })}
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <TeamCard team={soloTeam} currentUserId={userData?.id} onCustomize={setEditingTeam} />
                  </div>
                </section>
              )}

              <section>
                {filter === "all" && (
                  <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">
                    {t("squadsHeading", { defaultValue: "Squads" })}
                  </h2>
                )}
                {visibleTeams.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-10 text-center">
                    <Handshake size={32} className="mx-auto text-orange-400" aria-hidden="true" />
                    <p className="mt-3 font-semibold text-gray-900">
                      {t("noSquadsTitle", { defaultValue: "No squads here yet" })}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {t("noSquadsBody", {
                        defaultValue: "Play a Co-op or team PvP game with friends and your squad will show up here.",
                      })}
                    </p>
                    <Link
                      to="/"
                      className="mt-5 inline-flex min-h-[44px] items-center rounded-full bg-[#FF9200] px-5 text-sm font-bold text-[#120239]"
                    >
                      {t("viewGameHistory", { defaultValue: "View game history" })}
                    </Link>
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {visibleTeams.map((team) => (
                      <TeamCard key={team.id} team={team} currentUserId={userData?.id} onCustomize={setEditingTeam} />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        <aside className="space-y-6">
          <PlayerList
            title={t("playedWith", { defaultValue: "Played with" })}
            icon={Handshake}
            entries={played.teammates || []}
            emptyText={t("playedWithEmpty", { defaultValue: "Team up with a friend to see them here." })}
            renderMeta={(entry) =>
              t("gamesTogether", { defaultValue: "{{count}} games", count: entry.games })
            }
          />
          <PlayerList
            title={t("facedMost", { defaultValue: "Faced most" })}
            icon={Swords}
            entries={played.opponents || []}
            emptyText={t("facedMostEmpty", { defaultValue: "Challenge someone in PvP to start a rivalry." })}
            renderMeta={(entry) => `${entry.wins}-${entry.losses}`}
          />
        </aside>
      </div>

      {editingTeam && (
        <TeamCustomizeModal
          team={editingTeam}
          userId={userData?.id}
          onClose={() => setEditingTeam(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default Teams;
